// LWI-856 — Tiny wrapper around @datadog/browser-rum's user-identity API.
//
// We dynamic-import the SDK and cache the promise so this module doesn't
// pull RUM into the main bundle (preserves the lazy-load LWI-699 set up
// in `src/index.jsx`). All exports are fire-and-forget; failure to load
// or absence of the SDK is silently swallowed because RUM is optional.
//
// Calls made before `datadogRum.init()` runs (the SDK is lazy-loaded
// after first paint via requestIdleCallback) are buffered by the SDK
// and applied once init completes, so we don't need to gate on init
// here.

let rumPromise = null;

function getRum() {
  if (!rumPromise) {
    rumPromise = import("@datadog/browser-rum")
      .then((m) => m.datadogRum)
      .catch(() => null);
  }
  return rumPromise;
}

function toIdString(value) {
  return value != null ? String(value) : undefined;
}

/**
 * Attach the authenticated user to the current RUM session. Pass the
 * canonical user object (the one stored in AuthContext); we read `id`,
 * `email`, and `name` defensively. No-op if `user` is missing an id
 * (anonymous sessions stay anonymous).
 */
export async function rumSetUser(user) {
  const rum = await getRum();
  if (!rum || !user || user.id == null) return;
  rum.setUser({
    id: toIdString(user.id),
    email: user.email || undefined,
    name: user.name || undefined,
  });
}

/**
 * Clear the user identity from the RUM session. Called on logout and
 * on the admin-mode 401 cleanup path.
 */
export async function rumClearUser() {
  const rum = await getRum();
  rum?.clearUser?.();
}

/**
 * During impersonation we keep the *admin* as the RUM user (so audit
 * trails follow who actually clicked) and stash the impersonated target
 * here as a custom property surfaced as `usr.impersonating` in the RUM
 * Explorer. Pass `null` to clear.
 */
export async function rumSetImpersonating(target) {
  const rum = await getRum();
  if (!rum) return;
  if (target && target.id != null) {
    rum.setUserProperty?.("impersonating", {
      id: toIdString(target.id),
      email: target.email || undefined,
    });
  } else {
    rum.removeUserProperty?.("impersonating");
  }
}
